import { useEffect } from 'react'
import { Routes, Route } from 'react-router-dom'
import { useAppDispatch } from './hooks/redux'
import Layout from './components/Layout/Layout'
import MarinaLayout from './components/MarinaLayout'
import HomePage from './pages/HomePage'
import RoomsPage from './pages/RoomsPage'
import BookingPageNew from './pages/BookingPageNew'
import BookingPreviewPageRedirect from './pages/BookingPreviewPageRedirect'
import BookingPreviewPageNew from './pages/BookingPreviewPageNew'
import PaymentPage from './pages/PaymentPage'
import AdminDashboard from './pages/AdminDashboard'
import GuestDashboard from './pages/GuestDashboard'
import GuestBookingsPage from './pages/GuestBookingsPage'
import GuestSettingsPage from './pages/GuestSettingsPage'
import SeasonalCalendarPage from './pages/SeasonalCalendarPage'
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import ProtectedRoute from './components/Auth/ProtectedRoute'
import { authResolved, setSession } from './store/slices/authSlice'
import { getCurrentPortalSession, hasGuestPortalSupabase, subscribeToPortalAuthChanges } from './services/guestPortalSupabase'
import RouteSeo from './components/Seo/RouteSeo'
import './styles/marina-integration.css'

function MainSiteRedirect() {
  useEffect(() => {
    const hash = window.location.hash || ''
    const search = window.location.search || ''
    const hashParams = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash)
    const searchParams = new URLSearchParams(search)
    const authType = (searchParams.get('type') || hashParams.get('type') || '').toLowerCase()
    const hasAuthPayload = ['access_token', 'refresh_token', 'token_hash', 'code', 'error', 'error_code'].some(
      (key) => searchParams.has(key) || hashParams.has(key),
    )

    if (authType === 'recovery') {
      window.location.replace(`/booking-system/reset-password${search}${hash}`)
      return
    }

    if (hasAuthPayload) {
      window.location.replace(`/booking-system/login${search}${hash}`)
      return
    }

    window.location.replace('/index.html')
  }, [])

  return null
}

function App() {
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (!hasGuestPortalSupabase()) {
      dispatch(authResolved())
      return
    }

    let active = true

    const applyResolvedSession = async () => {
      try {
        const sessionState = await getCurrentPortalSession()
        if (!active) {
          return
        }

        if (sessionState) {
          dispatch(setSession(sessionState))
          return
        }

        dispatch(setSession(null))
      } catch {
        if (!active) {
          return
        }

        dispatch(setSession(null))
      }
    }

    void applyResolvedSession()

    const unsubscribe = subscribeToPortalAuthChanges((sessionState) => {
      if (!active) {
        return
      }

      if (sessionState) {
        dispatch(setSession(sessionState))
        return
      }

      dispatch(setSession(null))
    })

    return () => {
      active = false
      unsubscribe()
    }
  }, [dispatch])

  return (
    <>
      <RouteSeo />
      <Routes>
      <Route path="/" element={<MainSiteRedirect />} />
      {/* Public booking routes with Marina branding */}
      <Route path="/booking-system" element={
        <MarinaLayout showHeader={false}>
          <HomePage />
        </MarinaLayout>
      } />
      <Route path="/booking-system/rooms" element={
        <MarinaLayout title="Accommodation Options" subtitle="Choose your perfect oceanfront unit" showHeader={false}>
          <RoomsPage />
        </MarinaLayout>
      } />
      <Route path="/booking-system/booking" element={
        <MarinaLayout title="Book Your Stay" subtitle="Secure your reservation in just a few clicks" showHeader={false}>
          <BookingPageNew />
        </MarinaLayout>
      } />
      <Route path="/booking-system/payment" element={
        <MarinaLayout title="Secure Payment" subtitle="Choose your payment method to complete your booking">
          <PaymentPage />
        </MarinaLayout>
      } />
      <Route path="/booking-system/booking-preview" element={<BookingPreviewPageRedirect />} />
      <Route path="/booking-system/booking-form" element={
        <MarinaLayout title="Booking Details" subtitle="Complete your reservation">
          <BookingPreviewPageNew />
        </MarinaLayout>
      } />
      
      {/* Auth routes without main navigation */}
      <Route path="/booking-system/login" element={
        <MarinaLayout title="Admin Login" subtitle="Access your booking management dashboard" showNavigation={false}>
          <LoginPage />
        </MarinaLayout>
      } />
      <Route path="/booking-system/register" element={
        <MarinaLayout title="Admin Registration" subtitle="Create your management account" showNavigation={false}>
          <RegisterPage />
        </MarinaLayout>
      } />
      <Route path="/booking-system/reset-password" element={
        <MarinaLayout title="Reset Password" subtitle="Create a new password for your Marina Glen account" showNavigation={false}>
          <ResetPasswordPage />
        </MarinaLayout>
      } />
      
      {/* Guest dashboard route */}
      <Route 
        path="/booking-system/dashboard" 
        element={
          <ProtectedRoute>
            <MarinaLayout title="Guest Dashboard" subtitle="Manage your bookings and preferences">
              <GuestDashboard />
            </MarinaLayout>
          </ProtectedRoute>
        } 
      />
      <Route
        path="/booking-system/my-bookings"
        element={
          <ProtectedRoute>
            <MarinaLayout title="My Bookings" subtitle="View and manage your reservations">
              <GuestBookingsPage />
            </MarinaLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/booking-system/settings"
        element={
          <ProtectedRoute>
            <MarinaLayout title="Account Settings" subtitle="Update your profile and password">
              <GuestSettingsPage />
            </MarinaLayout>
          </ProtectedRoute>
        }
      />
        
      {/* Admin routes with original layout */}        
      <Route
        path="/booking-system/admin"
        element={
          <ProtectedRoute adminOnly>
            <Layout>
              <AdminDashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/booking-system/admin/seasonal-calendar"
        element={
          <ProtectedRoute adminOnly>
            <Layout>
              <SeasonalCalendarPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<MainSiteRedirect />} />
    </Routes>
    </>
  )
}

export default App